<template>
  <div class="bg-white py-16">
    <div class="container mx-auto px-4">
      <!-- 标题区域 -->
      <div class="text-center mb-12">
        <!-- 优化移动端标题字体大小 -->
        <h1 class="text-4xl lg:text-4xl md:text-3xl sm:text-xl font-bold text-gray-900 mb-2 italic leading-tight">
          {{ t('productionSystem.title') }}
        </h1>
        <h2 class="text-2xl lg:text-2xl md:text-xl sm:text-base font-semibold text-gray-800 mb-2 leading-relaxed">
          {{ t('productionSystem.subtitle') }}
        </h2>
        <p class="text-lg lg:text-lg md:text-base sm:text-sm text-gray-700 mb-2 leading-relaxed px-4 sm:px-2">
          {{ t('productionSystem.description') }}
        </p>

        <!-- 生产范围 -->
        <div class="mb-12 lg:mb-12 md:mb-10 sm:mb-6">
          <p class="text-xs lg:text-xs md:text-xs sm:text-xs text-gray-600 mb-2 px-4 sm:px-2">
            <span class="font-medium">{{ t('productionSystem.scope.title') }}</span>
            {{ t('productionSystem.scope.items') }}
          </p>
        </div>
      </div>

      <!-- 产品卡片轮播区域 -->
      <div class="relative">
        <swiper
          :modules="swiperModules"
          :slides-per-view="5"
          :space-between="30"
          :pagination="{ clickable: true, dynamicBullets: false }"
          :breakpoints="{
            320: { slidesPerView: 1, spaceBetween: 12 },
            480: { slidesPerView: 1.5, spaceBetween: 15 },
            640: { slidesPerView: 2, spaceBetween: 18 },
            768: { slidesPerView: 2.5, spaceBetween: 22 },
            1024: { slidesPerView: 4, spaceBetween: 28 },
            1280: { slidesPerView: 5, spaceBetween: 30 }
          }"
          class="productSwiper"
        >
          <swiper-slide v-for="(product, index) in products" :key="index">
            <div class="bg-gray-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 h-96 lg:h-96 md:h-80 sm:h-64">
              <!-- 产品图片区域 -->
              <div class="h-64 lg:h-64 md:h-52 sm:h-36 bg-gradient-to-br from-amber-50 to-orange-100 p-8 lg:p-8 md:p-6 sm:p-3 flex items-center justify-center">
                <div class="w-50 h-60 lg:w-50 lg:h-60 md:w-40 md:h-48 sm:w-28 sm:h-30 rounded-lg relative overflow-hidden">
                  <img
                    :src="getImagePath(index + 1)"
                    :alt="product.type"
                    class="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
              </div>

              <!-- 产品信息区域 -->
              <div class="h-32 lg:h-32 md:h-28 sm:h-28 p-6 lg:p-6 md:p-4 sm:p-2 bg-[#2f5c45] text-white flex flex-col justify-center">
                <p class="text-xs lg:text-xs md:text-xs sm:text-xs leading-relaxed opacity-90 text-center">
                  {{ t(`productionSystem.products.${index}.description`) }}
                </p>
              </div>
            </div>
          </swiper-slide>
        </swiper>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  import 'swiper/css'
  import 'swiper/css/navigation'
  import 'swiper/css/pagination'
  import 'swiper/css/scrollbar'

  const { t } = useI18n()
  const swiperModules = [Pagination, Navigation, Scrollbar, A11y]

  // 动态获取图片路径的函数
  const getImagePath = (imageNumber: number) => {
    try {
      // 使用动态导入来获取图片
      return new URL(
        `../../../assets/lunzi/${imageNumber}.png`,
        import.meta.url
      ).href
    } catch (error) {
      console.warn(`Image not found: lunzi/${imageNumber}.png`)
      // 返回默认图片或占位符
      return new URL('../../../assets/lunzi/1.png', import.meta.url).href
    }
  }

  // 产品数据，通过索引对应国际化键
  const products = computed(() => {
    return Array.from({ length: 10 }, (_, index) => ({
      id: index,
      image: getImagePath(index + 1),
      type: t(`productionSystem.products.${index}.type`),
      description: t(`productionSystem.products.${index}.description`)
    }))
  })
</script>

<style scoped>
  .productSwiper {
    padding: 20px 0 60px;
  }

  .swiper-pagination {
    position: relative !important;
    margin-top: 20px;
  }

  .swiper-pagination-bullet {
    width: 10px;
    height: 10px;
    background: #cbd5e1;
    opacity: 1 !important;
    margin: 0 4px;
  }

  :deep(.swiper-pagination-bullet-active) {
    background-color: #2f5c45 !important;
  }

  .swiper-slide {
    height: auto;
  }

  /* PC端样式保持不变 */
  @media (min-width: 1025px) {
    .productSwiper {
      padding: 20px 0 60px;
    }
    
    /* 确保PC端所有原有样式保持不变 */
    .text-4xl {
      font-size: 2.25rem;
      line-height: 2.5rem;
    }
    
    .text-2xl {
      font-size: 1.5rem;
      line-height: 2rem;
    }
    
    .text-lg {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
  }

  /* 平板端优化 */
  @media (min-width: 769px) and (max-width: 1024px) {
    .py-16 {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    .mb-12 {
      margin-bottom: 2.5rem;
    }
  }

  /* 移动端响应式优化 */
  @media (max-width: 768px) {
    .py-16 {
      padding-top: 2rem;
      padding-bottom: 2rem;
    }
    
    .mb-12 {
      margin-bottom: 1.5rem;
    }
    
    .productSwiper {
      padding: 15px 0 45px;
    }
    
    .swiper-pagination {
      margin-top: 15px;
    }
    
    .swiper-pagination-bullet {
      width: 8px;
      height: 8px;
      margin: 0 3px;
    }
    
    /* 移动端标题字体进一步优化 */
    .text-4xl {
      font-size: 1.25rem !important;
      line-height: 1.75rem !important;
    }
    
    .text-2xl {
      font-size: 1rem !important;
      line-height: 1.5rem !important;
    }
    
    .text-lg {
      font-size: 0.875rem !important;
      line-height: 1.25rem !important;
    }
  }

  /* 小屏手机优化 */
  @media (max-width: 480px) {
    .py-16 {
      padding-top: 1.5rem;
      padding-bottom: 1.5rem;
    }
    
    .productSwiper {
      padding: 10px 0 35px;
    }
    
    .container {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
    
    /* 小屏手机标题字体进一步缩小 */
    .text-4xl {
      font-size: 1.125rem !important;
      line-height: 1.5rem !important;
    }
    
    .text-2xl {
      font-size: 0.9rem !important;
      line-height: 1.25rem !important;
    }
    
    .text-lg {
      font-size: 0.8rem !important;
      line-height: 1.125rem !important;
    }
    
    .text-xs {
      font-size: 0.7rem !important;
    }
  }

  /* 超小屏优化 */
  @media (max-width: 360px) {
    .text-4xl {
      font-size: 1rem !important;
      line-height: 1.375rem !important;
    }
    
    .text-2xl {
      font-size: 0.85rem !important;
      line-height: 1.125rem !important;
    }
    
    .text-lg {
      font-size: 0.75rem !important;
      line-height: 1rem !important;
    }
  }

  /* 触摸设备优化 */
  @media (hover: none) and (pointer: coarse) {
    .swiper-slide {
      -webkit-tap-highlight-color: transparent;
    }
    
    .swiper-pagination-bullet {
      width: 12px;
      height: 12px;
    }
  }

  /* 横屏模式优化 */
  @media (max-width: 768px) and (orientation: landscape) {
    .py-16 {
      padding-top: 1rem;
      padding-bottom: 1rem;
    }
    
    .mb-12 {
      margin-bottom: 1rem;
    }
  }
</style>
